import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import type { AgentClientInfo } from "shared/AgentAccess";
import { normalizeUserCode, USER_CODE_PATTERN } from "shared/AgentAccess";
import { decideAgentDevice, lookupAgentDevice } from "src/CalmLensApi";
import AgentApproval, {
  type AgentApprovalDecision,
} from "src/components/auth/AgentApproval/AgentApproval";
import { agentApprovalError } from "src/components/auth/AgentApproval/AgentApprovalError";
import Button from "src/components/common/Button/Button";
import Callout from "src/components/common/Callout/Callout";
import ContentPanel from "src/components/common/ContentPanel/ContentPanel";
import FormLayout from "src/components/form/FormLayout/FormLayout";
import FormSection from "src/components/form/FormSection/FormSection";
import TextField from "src/components/form/TextField/TextField";
import { useExtracted, useTranslateText } from "#i18n";

// Device sign-in (RFC 8628) for the CLI: `calmlens login` shows a code and opens this page.
export const Route = createFileRoute("/_authed/device")({
  component: DevicePage,
});

function DevicePage() {
  const t = useExtracted();
  const translateText = useTranslateText();
  // The code is always typed: a link can't carry one, so a sign-in someone else started can't
  // arrive here ready to approve.
  const [code, setCode] = useState("");
  const [client, setClient] = useState<AgentClientInfo>();
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [decided, setDecided] = useState<"approved" | "denied">();

  const lookup = async (userCode: string) => {
    setBusy(true);
    setError("");
    try {
      setClient((await lookupAgentDevice.submit({ body: { userCode } })).data);
    } catch (error) {
      setError(agentApprovalError(error, t("Unable to find that code."), translateText));
    } finally {
      setBusy(false);
    }
  };

  const decide = async (approve: boolean, decision?: AgentApprovalDecision) => {
    setBusy(true);
    setError("");
    try {
      const { data } = await decideAgentDevice.submit({
        body: { userCode: code, approve, projectId: decision?.projectId, scopes: decision?.scopes },
      });
      setDecided(data.status);
    } catch (error) {
      setError(
        agentApprovalError(error, t("Unable to save your answer. Try again."), translateText),
      );
    } finally {
      setBusy(false);
    }
  };

  if (decided)
    return (
      <main className="mx-auto max-w-xl px-4 py-16">
        <Callout role="status" color={decided === "approved" ? "success" : "neutral"}>
          {decided === "approved"
            ? t("{app} is connected. Return to your terminal.", { app: client?.clientName ?? "" })
            : t("The sign-in was declined. You can close this page.")}
        </Callout>
      </main>
    );
  if (client)
    return (
      <AgentApproval
        clientName={client.clientName}
        deviceCode={normalizeUserCode(code)}
        returnTo="/device"
        allowWrite={client.requestedScopes?.includes("write") ?? true}
        error={error}
        busy={busy}
        onApprove={(decision) => void decide(true, decision)}
        onDeny={() => void decide(false)}
      />
    );
  return (
    <main className="mx-auto max-w-xl px-4 py-16">
      <ContentPanel>
        <ContentPanel.Header>
          <ContentPanel.Heading>
            <ContentPanel.Title>{t("Connect a device")}</ContentPanel.Title>
            <ContentPanel.Description>
              {t("Enter the code shown in your terminal.")}
            </ContentPanel.Description>
          </ContentPanel.Heading>
        </ContentPanel.Header>
        <ContentPanel.Content>
          <form
            onSubmit={(event) => {
              event.preventDefault();
              void lookup(code);
            }}
          >
            <FormLayout>
              <FormSection>
                <TextField
                  name="code"
                  label={t("Code")}
                  value={code}
                  onValueChange={(value) => setCode(value ?? "")}
                  autoComplete="off"
                />
                {error && (
                  <Callout role="alert" color="danger" size="small">
                    {error}
                  </Callout>
                )}
              </FormSection>
              <FormLayout.Actions>
                <Button
                  type="submit"
                  label={t("Continue")}
                  loading={busy}
                  disabled={!USER_CODE_PATTERN.test(normalizeUserCode(code))}
                />
              </FormLayout.Actions>
            </FormLayout>
          </form>
        </ContentPanel.Content>
      </ContentPanel>
    </main>
  );
}
